import { useInkind } from '@gik/api/inkinds/inkind';
import { translationKeys } from '@gik/calendar/components/Calendar/i18n/en';
import { CheckoutLauncher } from '@gik/checkout/components/CheckoutLauncher/CheckoutLauncher';
import { createCartItemFromProduct } from '@gik/checkout/utils/productUtils';
import type { Product } from '@gik/core/models/gik/Product';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button } from '@gik/ui/Button';
import { HTMLParser } from '@gik/ui/HTMLParser';
import { WordBone } from '@gik/ui/SkeletonLoader';
import type { UIButtonVariant } from '@gik/ui/types';
import { UI } from '@gik/ui/UIManager';
import loadable from '@loadable/component';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { InkindPageViewHistoryItem } from '../InkindPageViewRecorder/InkindPageViewRecorder';

const Format = loadable.lib(() => import('@gik/core/utils/format'));

interface IGiftBoxOptionProps {
  title: string;
  inkindRouteId: string;
  product: Product | undefined;
  inkindPageViewHistory: InkindPageViewHistoryItem[];
  append?: string;
  prepend?: string;
  deluxe?: boolean;
  isSamplePage?: boolean;
}

export default function GiftBoxOption({
  title,
  prepend,
  append,
  product,
  isSamplePage,
  inkindRouteId,
  inkindPageViewHistory,
  deluxe,
}: IGiftBoxOptionProps) {
  const { t } = useTranslation();
  const { data: inkind } = useInkind(inkindRouteId);
  const giftboxCart = product ? [createCartItemFromProduct(product, inkind)] : [];
  const buttonVariant: UIButtonVariant = deluxe ? 'deluxe' : 'primary';

  const bem = bemBlock('gift-box-option');

  return (
    <Format fallback={0}>
      {({ formatCurrency }) => {
        return (
          <section className={bem(undefined, deluxe ? 'deluxe' : 'standard')}>
            <div className={bem('title-wrapper')}>
              {prepend && (
                <p className={bem('prepend')}>
                  <HTMLParser rawHtml={prepend} />
                </p>
              )}
              <p className={bem('title')}>
                <HTMLParser rawHtml={title} />
              </p>
            </div>
            {append && (
              <p className={bem('append')}>
                <HTMLParser rawHtml={append} />
              </p>
            )}
            <div className={bem('footer') /*TODO: use <footer> after SPA migration */}>
              <p className={bem('price')}>
                {product && <>{formatCurrency(product.price)}</>}
                {!product && <WordBone width={60} />}
              </p>
              <CheckoutLauncher
                cachedProducts={product ? [product] : undefined}
                cart={giftboxCart}
                initialInkindRouteId={inkindRouteId}
                skipProductsCheckoutStep={false}
                inkindPageViewHistory={inkindPageViewHistory}
                child={(onClick, isLoading) => (
                  <Button
                    variant={isLoading ? 'primary' : buttonVariant}
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={async (ev: React.MouseEvent<HTMLElement, MouseEvent>) => {
                      if (isSamplePage) {
                        return await UI.alert(t(translationKeys.samplePageWarning));
                      }

                      return onClick(ev);
                    }}
                  >
                    SEND NOW
                  </Button>
                )}
                initiatedOn="inkindPage"
              />
            </div>
          </section>
        );
      }}
    </Format>
  );
}
